import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  FlaskConical,
  HardDrive,
  Users, 
  Play, 
  Square, 
  Trash2,
  X,
  Activity,
  ExternalLink,
  Search,
  Upload,
  FileArchive,
  Check,
  Loader,
  Calendar,
  Lock,
  Unlock,
  Video,
  VideoOff,
  FileCode,
  Clock,
  AlertCircle,
  CheckCircle2,
  Circle,
  User,
  CalendarClock
} from 'lucide-react';
import { labs, sections } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generateTermOptions } from '../utils/termUtils';
import LabFilterBar from '../components/LabFilterBar';
import LabListView from '../components/LabListView';
import {
  readFiltersFromParams, writeFiltersToParams, applyFilters, sortLabs,
  countActiveFilters, toggleFacetValue, clearFilters, isPastDue, isClosedWithRunning, groupLabs,
  closurePlan,
} from '../utils/labFilters';
import { Dialog, DialogHeader, DialogBody, DialogFooter, FormSection, Field, SwitchRow, hasOpenDialog } from '../components/Dialog';
import ClipboardPolicyField from '../components/ClipboardPolicyField';
import D2LSessionReminderSettings from '../components/D2LSessionReminderSettings';
import { DEFAULT_D2L_REMINDER_INTERVAL } from '../utils/d2lSessionReminder';
import LabNamingFields from '../components/LabNamingFields';
import { namingError, namingPayload, previewLabName } from '../utils/labNaming';
import { ImageOptionGroup, ImageMemoryWarning, useImageCatalog } from '../components/ImagePicker';
import { suggestEntryForCourse, findDefaultEntry, formatMemory } from '../utils/imageCatalog';
import { javaFileMovesNotice, missingReferencesNotice } from '../utils/fileUtils';
import useEnvironmentStats from '../hooks/useEnvironmentStats';
import { countLabel, withEnvironmentStats, EMPTY_ENVIRONMENT_STATS } from '../utils/environmentStats';
import EnvironmentStatsStatus, { AdditionalEnvironmentStates } from '../components/EnvironmentStatsStatus';

function Labs() {
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();

  // Filter state lives in the URL, so a filtered view is a link that can be
  // bookmarked or sent to a colleague. Mirrors the pattern already used in Sections.
  const [searchParams, setSearchParams] = useSearchParams();
  const parsed = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);
  const { active, search, sort, groupBy } = parsed;

  // View density: URL wins so a link carries it, otherwise the user's last
  // choice, otherwise cards.
  const [storedView, setStoredView] = useState(
    () => localStorage.getItem('labs.view') || 'cards'
  );
  const view = parsed.view || storedView;

  const updateFilters = (next) => {
    setSearchParams(
      writeFiltersToParams({ active, search, sort, view, groupBy, ...next }),
      { replace: true }
    );
  };

  const changeView = (v) => {
    setStoredView(v);
    localStorage.setItem('labs.view', v);
    updateFilters({ view: v });
  };

  const [collapsedGroups, setCollapsedGroups] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const searchRef = useRef(null);

  const { data: labsData, isLoading } = useQuery({
    queryKey: ['labs'],
    queryFn: () => labs.list(),
    refetchInterval: 30000,
  });

  const statsQuery = useEnvironmentStats();
  const labsList = useMemo(() => (labsData || []).map(lab => withEnvironmentStats(lab, statsQuery.data)), [labsData, statsQuery.data]);

  // One clock read per render pass keeps "past due" consistent across every
  // pill count, the sort and the row badges.
  const now = useMemo(() => new Date(), [labsList, searchParams]);

  const filteredLabs = useMemo(
    () => sortLabs(applyFilters(labsList, { active, search, now }), sort, now),
    [labsList, active, search, sort, now]
  );

  const hasFilters = countActiveFilters(active) > 0 || Boolean(search);
  const groups = useMemo(() => groupLabs(filteredLabs, groupBy), [filteredLabs, groupBy]);

  // Selection only ever refers to labs currently visible, so a hidden lab can
  // never be caught by a bulk action.
  const visibleIds = useMemo(() => filteredLabs.map((l) => l.id), [filteredLabs]);
  const selection = useMemo(
    () => selectedIds.filter((id) => visibleIds.includes(id)),
    [selectedIds, visibleIds]
  );
  const selectedLabs = useMemo(
    () => filteredLabs.filter((l) => selection.includes(l.id)),
    [filteredLabs, selection]
  );

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectMany = (ids, on) =>
    setSelectedIds((prev) => (on ? [...new Set([...prev, ...ids])] : prev.filter((x) => !ids.includes(x))));

  // Keyboard: "/" focuses search, Escape clears the current filters.
  useEffect(() => {
    const onKey = (e) => {
      if (hasOpenDialog()) return;
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape' && !typing && hasFilters) {
        updateFilters({ active: clearFilters(), search: '' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="animate-fade-in">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 24 
      }}>
        <div>
          <h1 style={{ marginBottom: 8 }}>Labs</h1>
          <p className="text-muted">Manage your lab environments</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} />
          Create Lab
        </button>
      </div>

      <EnvironmentStatsStatus stats={statsQuery.data} error={statsQuery.isError} loading={statsQuery.isPending} />
      {/* Search */}
      <div style={{ marginBottom: 24, position: 'relative' }} role="search">
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)'
          }}
          aria-hidden="true"
        />
        <input
          type="search"
          placeholder="Search by name, course, section or owner..."
          value={search}
          onChange={(e) => updateFilters({ search: e.target.value })}
          style={{ paddingLeft: 40 }}
          ref={searchRef}
          aria-label="Search labs (press / to focus)"
        />
      </div>

      {/* Faceted filters. Hidden until there is something to filter. */}
      {!isLoading && labsList.length > 0 && (
        <LabFilterBar
          labs={labsList}
          active={active}
          search={search}
          sort={sort}
          now={now}
          resultCount={filteredLabs.length}
          onToggleFacet={(key, value) => updateFilters({ active: toggleFacetValue(active, key, value) })}
          onClear={() => updateFilters({ active: clearFilters(), search: '' })}
          onSortChange={(value) => updateFilters({ sort: value })}
          view={view}
          onViewChange={changeView}
          groupBy={groupBy}
          onGroupByChange={(value) => updateFilters({ groupBy: value })}
          currentOwnerName={user?.name}
          onApplyView={(v) => updateFilters({ active: v.state.active, sort: v.state.sort })}
        />
      )}

      {/* Bulk actions appear only when something is selected, and name exactly
          what will happen before it happens. */}
      {selection.length > 0 && (
        <div
          role="region"
          aria-label="Bulk actions"
          style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
            padding: '10px 16px', marginBottom: 16, borderRadius: 10,
            background: 'var(--badge-info-bg)', border: '1px solid var(--accent-primary)',
          }}
        >
          <strong style={{ fontSize: 13.5, color: 'var(--accent-primary)' }}>
            {selection.length} lab{selection.length === 1 ? '' : 's'} selected
          </strong>
          <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setSelectedIds([])}>
            Clear selection
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => setBulkAction('stop')}
          >
            <Square size={13} /> Stop all containers
          </button>
        </div>
      )}

      {/* Labs grid */}
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }} role="status" aria-label="Loading labs">
          <div className="spinner" style={{ margin: '0 auto' }} aria-hidden="true" />
          <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Loading labs...</span>
        </div>
      ) : filteredLabs.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <FlaskConical size={48} style={{ marginBottom: 16, opacity: 0.3 }} aria-hidden="true" />
          <h3 style={{ marginBottom: 8 }}>
            {hasFilters ? 'No labs match these filters' : 'No labs found'}
          </h3>
          <p className="text-muted" style={{ marginBottom: 24 }}>
            {hasFilters
              ? `${labsList.length} lab${labsList.length === 1 ? '' : 's'} exist — the current combination excludes them all.`
              : 'Create your first lab to get started'}
          </p>
          {hasFilters ? (
            <button
              className="btn"
              onClick={() => updateFilters({ active: clearFilters(), search: '' })}
            >
              <X size={16} />
              Clear filters
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Create Lab
            </button>
          )}
        </div>
      ) : view === 'cards' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {groups.map((group) => (
            <div key={group.key}>
              {group.label && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  paddingBottom: 6, borderBottom: '1px solid var(--border-primary)',
                }}>
                  <button
                    type="button"
                    onClick={() => setCollapsedGroups((p) => p.includes(group.key) ? p.filter((k) => k !== group.key) : [...p, group.key])}
                    aria-expanded={!collapsedGroups.includes(group.key)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      color: 'var(--text-primary)', fontSize: 14, fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                      {collapsedGroups.includes(group.key) ? '▶' : '▼'}
                    </span>
                    {group.label}
                  </button>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)',
                    background: 'var(--bg-tertiary)', borderRadius: 999, padding: '1px 7px',
                  }}>
                    {group.labs.length}
                  </span>
                </div>
              )}
              {!collapsedGroups.includes(group.key) && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: 16,
                }}>
                  {group.labs.map(lab => (
                    <LabCard key={lab.id} lab={lab} now={now} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <LabListView
          groups={groups}
          now={now}
          view={view}
          selectedIds={selection}
          onSelect={toggleSelect}
          onSelectGroup={selectMany}
          collapsedGroups={collapsedGroups}
          onToggleGroup={(key) => setCollapsedGroups((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key])}
        />
      )}

      {/* Confirm before a mass mutation, listing exactly which labs are affected. */}
      {bulkAction === 'stop' && (
        <BulkStopModal
          labs={selectedLabs}
          onClose={() => setBulkAction(null)}
          onDone={() => {
            setBulkAction(null);
            setSelectedIds([]);
            queryClient.invalidateQueries({ queryKey: ['labs'] });
          }}
        />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <CreateLabModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['labs'] });
          }}
        />
      )}
    </div>
  );
}

// Convert UDST term code to display text
function formatTermDisplay(termCode) {
  if (!termCode || termCode.length !== 4) return termCode;
  const yy = termCode.substring(1, 3);
  const t = termCode.charAt(3);
  const termNames = { '8': 'FALL', '1': 'WINTER', '5': 'SPRING' };
  return termNames[t] ? `${termNames[t]} ${yy}` : termCode;
}

function LabCard({ lab, now }) {
  const stats = lab.containerStats || EMPTY_ENVIRONMENT_STATS;
  const queryClient = useQueryClient();

  const startMutation = useMutation({
    mutationFn: () => labs.start(lab.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['labs'] }),
  });

  const stopMutation = useMutation({
    mutationFn: () => labs.stop(lab.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['labs'] }),
  });

  // Status styling
  const getStatusStyle = (status) => {
    switch (status) {
      case 'open': return { bg: 'var(--badge-success-bg)', color: 'var(--accent-success)', icon: CheckCircle2 };
      case 'closed': return { bg: 'var(--badge-danger-bg)', color: 'var(--accent-danger)', icon: AlertCircle };
      default: return { bg: 'var(--badge-warning-bg)', color: 'var(--accent-warning)', icon: Circle };
    }
  };

  const statusStyle = getStatusStyle(lab.status);
  const StatusIcon = statusStyle.icon;

  // Encode "something is wrong here" in form, not just in a date string, so it
  // reads at a glance in a wall of cards.
  // Last-known faculty snapshot-storage usage, recorded by the snapshot
  // scheduler. Null until that lab's pod has been visited at least once.
  const storage = lab.facultyPodStorage;
  const closure = closurePlan(lab);
  const gib = (bytes) => (bytes / 1024 / 1024 / 1024).toFixed(1);

  const attentionReason = isPastDue(lab, now)
    ? 'Past due and still open'
    : isClosedWithRunning(lab)
      ? `Closed, but ${stats.running} container${stats.running === 1 ? '' : 's'} still running`
      : null;

  return (
    <div
      className="card"
      style={{
        padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%',
        boxShadow: attentionReason ? 'inset 3px 0 0 var(--accent-danger)' : undefined,
      }}
    >
      {attentionReason && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '7px 20px', fontSize: 12, fontWeight: 500,
          background: 'var(--badge-danger-bg)', color: 'var(--accent-danger)',
          borderBottom: '1px solid var(--border-primary)',
        }}>
          <AlertCircle size={13} aria-hidden="true" />
          {attentionReason}
        </div>
      )}
      {storage && storage.usagePercent >= 85 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '7px 20px', fontSize: 12, fontWeight: 500,
          background: storage.usagePercent >= 95 ? 'var(--badge-danger-bg)' : 'var(--badge-warning-bg)',
          color: storage.usagePercent >= 95 ? 'var(--accent-danger)' : 'var(--accent-warning)',
          borderBottom: '1px solid var(--border-primary)',
        }}>
          <HardDrive size={13} aria-hidden="true" />
          {storage.usagePercent >= 95
            ? `Snapshot storage full (${storage.usagePercent}%) — snapshots are failing`
            : `Snapshot storage ${storage.usagePercent}% full`}
        </div>
      )}
      <div style={{ padding: 20, flex: 1 }}>
        {/* Header with name and status */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: lab.status === 'open' 
              ? 'linear-gradient(135deg, var(--accent-success)30, var(--accent-primary)30)'
              : 'linear-gradient(135deg, var(--accent-purple)30, var(--accent-primary)30)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FlaskConical size={22} style={{ color: lab.status === 'open' ? 'var(--accent-success)' : 'var(--accent-purple)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <h3 style={{ fontSize: 15, margin: 0, fontWeight: 600 }}>
                {lab.display_name || lab.name}
              </h3>
              {/* Status Badge */}
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  background: statusStyle.bg,
                  color: statusStyle.color,
                  borderRadius: 4,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  textTransform: 'uppercase',
                }}
                role="status"
                aria-label={`Lab status: ${lab.status || 'Draft'}`}
              >
                <StatusIcon size={10} aria-hidden="true" />
                {lab.status || 'Draft'}
              </span>
              {lab.isSemesterLab && (
                <span style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  background: 'var(--badge-info-bg)',
                  color: 'var(--accent-primary)',
                  borderRadius: 4,
                  fontWeight: 600,
                }}>
                  SEMESTER
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {lab.term && (
                <span style={{
                  fontSize: 11,
                  padding: '2px 6px',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-muted)',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                  <Calendar size={10} />
                  {formatTermDisplay(lab.term)}
                </span>
              )}
              {lab.courseCode && (
                <code style={{ 
                  fontSize: 11, 
                  color: 'var(--text-muted)',
                  background: 'var(--bg-tertiary)',
                  padding: '2px 6px',
                  borderRadius: 4,
                }}>
                  {lab.courseCode}{lab.section ? `-S${lab.section}` : ''}
                </code>
              )}
            </div>
          </div>
        </div>

        {/* Feature badges row */}
        <div style={{ 
          display: 'flex', 
          gap: 6, 
          marginBottom: 12,
          flexWrap: 'wrap',
        }}>
          {/* Access Type */}
          <span style={{
            fontSize: 10,
            padding: '3px 8px',
            background: lab.requireD2LAccess ? 'var(--badge-info-bg)' : 'var(--badge-success-bg)',
            color: lab.requireD2LAccess ? 'var(--accent-purple)' : 'var(--accent-success)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontWeight: 500,
          }}>
            {lab.requireD2LAccess ? <Lock size={10} /> : <Unlock size={10} />}
            {lab.requireD2LAccess ? 'D2L Only' : 'Open Access'}
          </span>

          {/* Starter Code */}
          <span style={{
            fontSize: 10,
            padding: '3px 8px',
            background: lab.hasStarterCode ? 'var(--badge-info-bg)' : 'var(--bg-tertiary)',
            color: lab.hasStarterCode ? 'var(--accent-primary)' : 'var(--text-muted)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontWeight: 500,
          }}>
            <FileCode size={10} />
            {lab.hasStarterCode ? 'Starter Code' : 'No Starter'}
          </span>

          {/* Screen Recording */}
          <span style={{
            fontSize: 10,
            padding: '3px 8px',
            background: lab.screenRecordingEnabled ? 'var(--badge-success-bg)' : 'var(--bg-tertiary)',
            color: lab.screenRecordingEnabled ? 'var(--accent-success)' : 'var(--text-muted)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontWeight: 500,
          }}>
            {lab.screenRecordingEnabled ? <Video size={10} /> : <VideoOff size={10} />}
            {lab.screenRecordingEnabled ? 'Recording' : 'No Recording'}
          </span>
        </div>

        {/* Due Date if set */}
        {lab.dueDate && (
          <div style={{
            fontSize: 12,
            color: new Date(lab.dueDate) < new Date() ? 'var(--accent-danger)' : 'var(--text-secondary)',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            background: new Date(lab.dueDate) < new Date() ? 'var(--btn-danger-bg)' : 'var(--bg-tertiary)',
            borderRadius: 6,
          }}>
            <Clock size={12} />
            <span>Due: {new Date(lab.dueDate).toLocaleDateString('en-US', { 
              month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
            })}</span>
            {new Date(lab.dueDate) < new Date() && (
              <span style={{ marginLeft: 'auto', fontWeight: 600, fontSize: 10 }}>PAST DUE</span>
            )}
          </div>
        )}

        {/* When — and whether — this lab will release its resources.
            A due date alone is enough, because the scheduler falls back to
            dueDate + grace. What actually strands pods and volumes is having
            neither date, or having auto-close switched off — and that second
            case looked perfectly healthy here until now. */}
        {closure && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', marginTop: 8, borderRadius: 6, fontSize: 12,
            background: closure.kind === 'manual' ? 'var(--badge-danger-bg)' : 'var(--bg-tertiary)',
            color: closure.kind === 'manual' ? 'var(--accent-danger)' : 'var(--text-secondary)',
          }}>
            {closure.kind === 'manual'
              ? <AlertCircle size={12} aria-hidden="true" />
              : <CalendarClock size={12} aria-hidden="true" />}
            {closure.kind === 'manual' ? (
              <span>
                <strong>No automatic close</strong>
                {closure.reason === 'auto-close-off'
                  ? ' — auto-close is turned off'
                  : ' — no due or close date set'}
              </span>
            ) : (
              <>
                <span>
                  Closes {new Date(closure.at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                {closure.source === 'dueDate' && (
                  <span
                    style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-muted)' }}
                    title={`No close date set, so the lab closes ${closure.graceMinutes} minutes after the due date.`}
                  >
                    from due date
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* Creator */}
        {lab.createdBy && (
          <div style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}>
            <User size={12} />
            <span>{lab.createdBy.name || lab.createdBy.email}</span>
          </div>
        )}

        {/* Stats */}
        <div style={{
          display: 'flex',
          gap: 12,
          fontSize: 13,
          color: 'var(--text-secondary)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Users size={14} />
            <strong>{lab.studentCount || 0}</strong> students
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Activity size={14} style={{ color: 'var(--accent-success)' }} />
            <strong style={{ color: stats.running > 0 ? 'var(--accent-success)' : 'inherit' }}>{countLabel(stats.running)}</strong> running env
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Square size={14} style={{ color: 'var(--accent-warning)' }} />
            <strong>{countLabel(stats.stopped)}</strong> stopped
          </span>
        </div>
        <AdditionalEnvironmentStates stats={stats} />

        {/* Snapshot storage. A meter as well as a number, so a filling volume
            is visible at a glance rather than needing the figure read. */}
        {storage && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
                <HardDrive size={13} aria-hidden="true" />
                Snapshot storage
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', fontSize: 12,
                color: storage.usagePercent >= 95 ? 'var(--accent-danger)'
                  : storage.usagePercent >= 85 ? 'var(--accent-warning)' : 'var(--text-secondary)',
              }}>
                {gib(storage.usedBytes)} / {gib(storage.totalBytes)} Gi
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={storage.usagePercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Snapshot storage used"
              style={{ height: 5, borderRadius: 999, background: 'var(--bg-tertiary)', overflow: 'hidden' }}
            >
              <div style={{
                width: `${Math.min(100, storage.usagePercent)}%`, height: '100%', borderRadius: 999,
                background: storage.usagePercent >= 95 ? 'var(--accent-danger)'
                  : storage.usagePercent >= 85 ? 'var(--accent-warning)' : 'var(--accent-success)',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        borderTop: '1px solid var(--border-secondary)',
      }}>
        <Link
          to={`/labs/${lab.id}`}
          style={{
            flex: 1,
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            borderRight: '1px solid var(--border-secondary)',
            transition: 'background 0.15s ease',
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <ExternalLink size={15} />
          View Lab
        </Link>
        <button
          onClick={() => stats.running > 0 ? stopMutation.mutate() : startMutation.mutate()}
          disabled={startMutation.isPending || stopMutation.isPending || lab.studentCount === 0}
          style={{
            flex: 1,
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 13,
            fontWeight: 500,
            color: lab.studentCount === 0 
              ? 'var(--text-muted)' 
              : stats.running > 0 ? 'var(--accent-warning)' : 'var(--accent-success)',
            background: 'transparent',
            border: 'none',
            cursor: lab.studentCount === 0 ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s ease',
            opacity: lab.studentCount === 0 ? 0.5 : 1,
          }}
          onMouseOver={(e) => { if (lab.studentCount > 0) e.currentTarget.style.background = 'var(--bg-tertiary)' }}
          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          title={lab.studentCount === 0 ? 'No students enrolled' : ''}
        >
          {stats.running > 0 ? (
            <>
              <Square size={15} />
              Stop All
            </>
          ) : (
            <>
              <Play size={15} />
              Start All
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Fallback when creation uses the platform default or the catalogue has no
// recommendation. A selected non-default image supplies its own sizing in the API.
const NEW_LAB_DEFAULT_MEMORY = '3Gi';

function CreateLabModal({ onClose, onCreated }) {
  const { terms, defaultTerm } = useMemo(() => generateTermOptions(), []);
  
  const [term, setTerm] = useState(defaultTerm);
  const [courseCode, setCourseCode] = useState('');
  const [section, setSection] = useState('');
  const [naming, setNaming] = useState({ activityType: 'lab', activityNumber: '1', customActivityType: '' });
  const [isSemesterLab, setIsSemesterLab] = useState(false);
  const [description, setDescription] = useState('');
  const [starterCodeFile, setStarterCodeFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  // Access & Deadline settings
  const [requireD2LAccess, setRequireD2LAccess] = useState(true);
  // Follow D2L access only until the faculty member explicitly chooses.
  const [d2lReminderChoice, setD2LReminderChoice] = useState(null);
  const [d2lReminderInterval, setD2LReminderInterval] = useState(DEFAULT_D2L_REMINDER_INTERVAL);
  const d2lSessionReminder = { enabled: d2lReminderChoice ?? requireD2LAccess, intervalMinutes: d2lReminderInterval };
  const [autoCloseOnDeadline, setAutoCloseOnDeadline] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [allowWorkspaceDownload, setAllowWorkspaceDownload] = useState(true);
  const [allowWorkspaceUpload, setAllowWorkspaceUpload] = useState(false);
  const [clipboardPolicy, setClipboardPolicy] = useState('allow');

  // Section selector state
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [sectionMode, setSectionMode] = useState('select'); // 'select' or 'manual'
  const [provisionProgress, setProvisionProgress] = useState(null);

  // ---- Environment (container image) ----------------------------------------------------
  const { entries: imageEntries, available: imageCatalogAvailable, isLoading: imageCatalogLoading, unavailableReason: imageCatalogReason } = useImageCatalog();
  const [imageEntry, setImageEntry] = useState(null);
  // Once a human has picked, typing in the course code field must never move it.
  const imageTouched = useRef(false);
  const [imageSuggestedBy, setImageSuggestedBy] = useState('');
  // Use the same selection for the preview and the request. The API fills in the
  // current catalogue's recommended resources when it receives this image key.
  const chosenImageEntry = imageCatalogAvailable && imageEntry && !imageEntry.isDefault && !imageEntry.retired
    ? imageEntry : null;
  const chosenImageKey = chosenImageEntry?.key || null;
  const creationMemoryLimit = chosenImageEntry?.suggestedMemory || NEW_LAB_DEFAULT_MEMORY;

  useEffect(() => {
    if (!imageCatalogAvailable || imageTouched.current) return;
    const suggestion = suggestEntryForCourse(imageEntries, courseCode);
    if (suggestion) {
      setImageEntry(suggestion);
      setImageSuggestedBy(courseCode.toUpperCase());
    } else {
      setImageEntry(findDefaultEntry(imageEntries));
      setImageSuggestedBy('');
    }
  }, [imageCatalogAvailable, imageEntries, courseCode]);

  // Reset section selection when term or courseCode changes
  useEffect(() => {
    setSelectedSectionId(null);
    setSection('');
    setSectionMode('select');
  }, [term, courseCode]);

  // Fetch available sections when term and courseCode are set
  const { data: availableSections } = useQuery({
    queryKey: ['sections', term, courseCode?.toUpperCase()],
    queryFn: () => sections.list({ term, courseCode: courseCode?.toUpperCase() }),
    enabled: !!term && !!courseCode && courseCode.length >= 2,
  });

  const generatedName = previewLabName({ term, courseCode, section }, naming, isSemesterLab);

  // Get display text for selected term
  const selectedTermDisplay = useMemo(() => {
    const t = terms.find(t => t.code === term);
    return t ? t.display : term;
  }, [term, terms]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      // Create lab first
      const createResponse = await labs.create(data);
      const labId = createResponse.lab?.id || createResponse.lab?._id || createResponse.id || createResponse._id;
      
      // Upload starter code if provided
      let uploaded = null;
      if (starterCodeFile && labId) {
        uploaded = await labs.uploadStarterCodeToLab(labId, starterCodeFile);
      }

      // When a lab is created from a section, the backend auto-enrolls the roster but does NOT
      // create containers (the old flow created them via the Bulk Add modal). Restore that
      // behaviour: provision a container for every enrolled student that doesn't have one yet.
      if (selectedSectionId && labId) {
        try {
          const labDetail = await labs.get(labId);
          const roster = (labDetail.students || labDetail.lab?.students || []).filter(s => !s.container);
          if (roster.length > 0) {
            setProvisionProgress({ current: 0, total: roster.length, successCount: 0, failedCount: 0 });
            await labs.createContainersBulk(labId, roster, { parallelLimit: 8, onProgress: setProvisionProgress });
          }
        } catch (err) {
          // The lab IS created; provisioning is best-effort. Don't fail creation — the operator
          // can still create containers from the lab page.
          console.error('[Create Lab] Auto-provision containers failed:', err);
        }
      }

      return {
        ...createResponse,
        javaFilesMoved: uploaded?.javaFilesMoved,
        missingReferences: uploaded?.missingReferences,
        missingReferencesTotal: uploaded?.missingReferencesTotal,
      };
    },
    onSuccess: (result) => {
      setProvisionProgress(null);
      onCreated();
      // What the upload did to the starter code (Java files put in the folders their packages
      // name) and found in it (links to files it does not have); said once, in one alert.
      const parts = [];
      const notice = javaFileMovesNotice(result?.javaFilesMoved);
      if (notice) {
        const lines = notice.moves.slice(0, 8);
        if (notice.moves.length > 8) lines.push(`and ${notice.moves.length - 8} more`);
        parts.push(`${notice.headline}\n\n${lines.join('\n')}`);
      }
      const links = missingReferencesNotice(result?.missingReferences, result?.missingReferencesTotal, 5);
      if (links) {
        const lines = links.more > 0 ? [...links.lines, `and ${links.more} more`] : links.lines;
        parts.push(`${links.headline}\n\n${lines.join('\n')}\n\nThe lab page lists them under Starter Code.`);
      }
      if (parts.length) window.alert(`Starter code: ${parts.join('\n\n')}`);
    },
    onError: (err) => { setProvisionProgress(null); setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create lab'); },
  });

  // Closing mid-create would abandon a half-provisioned lab; the X and Escape
  // wait for the request instead.
  const closeUnlessBusy = createMutation.isPending ? undefined : onClose;
  // What a mistaken Escape or X would throw away. The image choice and the
  // starter ZIP arrive by click and file picker, not typing, so they are listed.
  const hasUnsavedInput = Boolean(
    courseCode || section || naming.activityType !== 'lab' || naming.activityNumber !== '1' || naming.customActivityType || description || dueDate || closeDate
    || starterCodeFile || imageTouched.current || isSemesterLab || term !== defaultTerm
    || !requireD2LAccess || !autoCloseOnDeadline || !allowWorkspaceDownload || allowWorkspaceUpload
    || !d2lSessionReminder.enabled || d2lReminderInterval !== DEFAULT_D2L_REMINDER_INTERVAL
    || clipboardPolicy !== 'allow'
  );

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      setError('Please upload a ZIP file');
      return;
    }
    
    setStarterCodeFile(file);
    setError('');
  };

  const handleRemoveFile = () => {
    setStarterCodeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!term) {
      setError('Please select a term');
      return;
    }
    
    if (!courseCode) {
      setError('Please enter a course code');
      return;
    }
    
    if (!isSemesterLab && namingError(naming)) {
      setError(namingError(naming));
      return;
    }
    
    // Semester labs require due date and close date
    if (isSemesterLab && !dueDate) {
      setError('Semester labs require a due date');
      return;
    }
    
    if (isSemesterLab && !closeDate) {
      setError('Semester labs require a close date');
      return;
    }
    
    // Only a deliberate, non-default choice sends containerConfig. Omitting it entirely means
    // a lab created with the default resolves to exactly the image it resolves to today —
    // the Lab model default, untouched — rather than to whatever ref the catalogue happens
    // to publish for "default" this week.
    createMutation.mutate({
      // The API generates assessment names; semester workspaces retain their
      // existing numberless naming and scheduling behavior.
      ...(isSemesterLab ? { name: generatedName, labNumber: null } : namingPayload(naming)),
      term,
      courseCode: courseCode.toUpperCase(),
      section: section || '',
      sectionId: selectedSectionId || undefined,
      isSemesterLab,
      description,
      requireD2LAccess,
      d2lSessionReminder,
      autoCloseOnDeadline,
      allowWorkspaceDownload,
      allowWorkspaceUpload,
      clipboardPolicy,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      closeDate: closeDate ? new Date(closeDate).toISOString() : null,
      ...(chosenImageKey ? { containerConfig: { imageKey: chosenImageKey } } : {}),
    });
  };

  const selectStyle = {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-primary)',
    borderRadius: 6,
    color: 'var(--text-primary)',
    fontSize: 14,
    cursor: 'pointer',
  };

  return (
    <Dialog size="xl" onClose={closeUnlessBusy} labelledBy="create-lab-title" isDirty={hasUnsavedInput}>
      <DialogHeader id="create-lab-title" title="Create New Lab" icon={<FlaskConical size={20} />} onClose={closeUnlessBusy}>
        {generatedName && (
          <span
            title="Auto-generated lab name"
            style={{
              fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600,
              color: 'var(--accent-primary)', background: 'var(--badge-info-bg)',
              border: '1px solid var(--accent-primary)', borderRadius: 999,
              padding: '4px 12px', overflowWrap: 'anywhere',
            }}
          >
            {generatedName}
          </span>
        )}
      </DialogHeader>

      <form className="dlg-form" onSubmit={handleSubmit} aria-label="Create new lab">
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}
          {provisionProgress && (
            <div role="status" aria-live="polite" style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: 'var(--badge-info-bg)', border: '1px solid var(--accent-primary)', fontSize: 14 }}>
              Provisioning containers: {provisionProgress.current || 0}/{provisionProgress.total || 0}
              {provisionProgress.failedCount > 0 ? ` — ${provisionProgress.failedCount} failed (you can retry from the lab page)` : ''}
            </div>
          )}

          {/* Identity */}
          <FormSection title="Identity">
            <Field label="Term" htmlFor="lab-term" required
              hint="UDST academic term (Fall starts August, Winter starts January, Spring starts May)">
              <select id="lab-term" value={term} onChange={(e) => setTerm(e.target.value)} style={selectStyle} required aria-required="true">
                {terms.map(t => (
                  <option key={t.code} value={t.code}>{t.display} ({t.code})</option>
                ))}
              </select>
            </Field>

            <div className="form-grid form-grid-21">
              <Field label="Course Code" htmlFor="lab-course-code" required>
                <input
                  id="lab-course-code"
                  type="text"
                  value={courseCode}
                  onChange={(e) => setCourseCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                  placeholder="INFS3201"
                  required
                  aria-required="true"
                  style={{ textTransform: 'uppercase' }}
                />
              </Field>
              <Field label="Section" htmlFor="lab-section">
                {sectionMode === 'manual' ? (
                  <>
                    <input
                      id="lab-section"
                      type="text"
                      value={section}
                      onChange={(e) => setSection(e.target.value)}
                      placeholder="1"
                    />
                    <button
                      type="button"
                      onClick={() => { setSectionMode('select'); setSection(''); setSelectedSectionId(null); }}
                      style={{ marginTop: 6, background: 'none', border: 'none', color: 'var(--accent-primary)', fontSize: 12, cursor: 'pointer', padding: 0 }}
                    >
                      Select from existing sections
                    </button>
                  </>
                ) : (
                  <>
                    <select
                      id="lab-section"
                      value={selectedSectionId || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__manual__') {
                          setSectionMode('manual');
                          setSelectedSectionId(null);
                          setSection('');
                        } else if (val) {
                          const sec = (availableSections || []).find(s => s._id === val);
                          if (sec) {
                            setSelectedSectionId(val);
                            setSection(sec.sectionNumber);
                          }
                        } else {
                          setSelectedSectionId(null);
                          setSection('');
                        }
                      }}
                      style={{ ...selectStyle, cursor: 'pointer' }}
                    >
                      <option value="">— Select section or enter manually —</option>
                      {(availableSections || []).map(s => (
                        <option key={s._id} value={s._id}>
                          S{s.sectionNumber}{s.name ? ` — ${s.name}` : ''} ({s.studentCount || 0} students)
                        </option>
                      ))}
                      <option value="__manual__">Enter section number manually...</option>
                    </select>
                    {selectedSectionId && (
                      <div style={{ marginTop: 8, padding: '6px 10px', background: 'var(--badge-info-bg)', borderRadius: 6, fontSize: 12, color: 'var(--accent-primary)' }}>
                        {(availableSections || []).find(s => s._id === selectedSectionId)?.studentCount || 0} students will be auto-enrolled
                      </div>
                    )}
                    {(!availableSections || availableSections.length === 0) && term && courseCode && (
                      <p className="field-hint">
                        No sections found for this course. Create sections from the Sections page to auto-enroll students.
                      </p>
                    )}
                  </>
                )}
              </Field>
            </div>

            {!isSemesterLab && <LabNamingFields value={naming} onChange={setNaming} disabled={createMutation.isPending} />}
            <p className="field-hint">The full name keeps the term, course and section. Choose the final activity type and number.</p>

            <Field label="Description" htmlFor="lab-description">
              <textarea
                id="lab-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the lab..."
                rows={2}
              />
            </Field>
          </FormSection>

          {/* Environment.
              Above scheduling deliberately: the runtime is an identity decision, not a
              preference. Starter code, the language server, and everything a student can
              actually run follow from it, so it belongs with the course and section rather
              than down among the dates. */}
          <FormSection title="Environment">
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px' }}>
              The toolchain every student in this lab gets. Starter code means nothing without
              it — a Flutter project in a Python environment will not build.
            </p>

            {imageCatalogLoading ? (
              <div role="status" aria-label="Loading environments" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                <span className="spinner" style={{ width: 16, height: 16 }} aria-hidden="true" />
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Loading environments…</span>
              </div>
            ) : imageCatalogAvailable ? (
              <>
                <ImageOptionGroup
                  entries={imageEntries}
                  selected={imageEntry}
                  onSelect={(entry) => { imageTouched.current = true; setImageSuggestedBy(''); setImageEntry(entry); }}
                  ariaLabel="Lab environment"
                  testIdPrefix="create-lab-image"
                />

                {imageSuggestedBy && imageEntry && (
                  <p role="status" style={{ fontSize: 12, color: 'var(--accent-primary)', marginTop: 10, marginBottom: 0 }}>
                    Preselected for {imageSuggestedBy}. Change it if this lab needs something else.
                  </p>
                )}

                <p role="status" data-testid="create-lab-resources" style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 0 }}>
                  Memory per student: <strong>{formatMemory(creationMemoryLimit)}</strong> limit.{' '}
                  {chosenImageEntry
                    ? 'The selected environment’s recommended settings are applied automatically when this lab is created.'
                    : 'Platform defaults are applied automatically when this lab is created.'}
                </p>
                <ImageMemoryWarning
                  entry={imageEntry}
                  memoryLimit={creationMemoryLimit}
                  suffix={'Raise "Memory per student" on the lab settings page before students start.'}
                />

                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, marginBottom: 0 }}>
                  You can change this later, but only environments created after the change pick
                  it up — Recreate All skips anything that is already running.
                </p>
              </>
            ) : (
              <p role="status" style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
                {imageCatalogReason} This lab will use the standard environment.
              </p>
            )}
          </FormSection>

          {/* Assignment & schedule */}
          <FormSection title="Assignment & Schedule">
            <SwitchRow
              id="lab-semester"
              title="Semester Lab"
              description="Students can access this lab anytime during the semester"
              checked={isSemesterLab}
              onChange={(e) => setIsSemesterLab(e.target.checked)}
            />

            {isSemesterLab && (
              <div style={{ marginTop: 16 }}>
                <div className="form-grid form-grid-2">
                  <Field label="Due Date" required>
                    <input
                      type="datetime-local"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      required={isSemesterLab}
                    />
                  </Field>
                  <Field label="Close Date" required>
                    <input
                      type="datetime-local"
                      value={closeDate}
                      onChange={(e) => setCloseDate(e.target.value)}
                      required={isSemesterLab}
                    />
                  </Field>
                </div>
                <SwitchRow
                  id="lab-autoclose"
                  title="Auto-close on deadline"
                  description="Automatically close the lab when the close date is reached"
                  checked={autoCloseOnDeadline}
                  onChange={(e) => setAutoCloseOnDeadline(e.target.checked)}
                />
              </div>
            )}
          </FormSection>

          {/* Access & security */}
          <FormSection title="Access & Security">
            <SwitchRow
              id="lab-d2l"
              title="Require D2L Access"
              description={requireD2LAccess
                ? 'Students must access through the D2L LMS link. Direct URL access is blocked.'
                : 'Students can access via email verification if not coming from D2L.'}
              checked={requireD2LAccess}
              onChange={(e) => setRequireD2LAccess(e.target.checked)}
            />
            <D2LSessionReminderSettings
              idPrefix="lab"
              value={d2lSessionReminder}
              onChange={change => {
                if (change.enabled !== undefined) setD2LReminderChoice(change.enabled);
                if (change.intervalMinutes !== undefined) setD2LReminderInterval(change.intervalMinutes);
              }}
              isCreate
            />
            <SwitchRow
              id="lab-download"
              title="Allow Workspace Download"
              description={allowWorkspaceDownload
                ? 'Students can download their workspace as a ZIP file from the IDE status bar.'
                : 'Workspace download is disabled. Students cannot export their work as ZIP.'}
              checked={allowWorkspaceDownload}
              onChange={(e) => setAllowWorkspaceDownload(e.target.checked)}
            />
            <SwitchRow
              id="lab-upload"
              title="Allow students to import existing code"
              description="Students can upload files, a folder, or a ZIP into a separate project in their workspace."
              checked={allowWorkspaceUpload}
              onChange={(e) => setAllowWorkspaceUpload(e.target.checked)}
            />
            <ClipboardPolicyField name="create-lab-clipboard" value={clipboardPolicy} onChange={setClipboardPolicy} />
          </FormSection>

          {/* Starter code */}
          <FormSection title="Starter Code">
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {!starterCodeFile ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                role="button"
                tabIndex={0}
                aria-label="Upload starter code ZIP file"
                style={{
                  border: '2px dashed var(--border-primary)',
                  borderRadius: 8,
                  padding: '24px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: 'var(--bg-tertiary)',
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.background = 'var(--badge-info-bg)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
              >
                <Upload size={32} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 14 }}>Click to upload a <strong>ZIP file</strong></p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Contains the starter code for students</p>
              </div>
            ) : (
              <div style={{ border: '1px solid var(--border-primary)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-tertiary)' }}>
                  <FileArchive size={24} style={{ color: 'var(--accent-primary)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{starterCodeFile.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{(starterCodeFile.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <Check size={20} style={{ color: 'var(--accent-success)' }} />
                  <button type="button" onClick={handleRemoveFile} aria-label="Remove uploaded file" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}>
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
            <p className="field-hint">Optional. Leave empty for an empty workspace.</p>
          </FormSection>
        </DialogBody>

        <DialogFooter>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={createMutation.isPending}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || isUploading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {createMutation.isPending ? <div className="spinner" /> : <><Plus size={18} /> Create Lab</>}
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export default Labs;


/**
 * Confirmation for a bulk stop. Mass mutations get an explicit list of what
 * will be affected and a running total of containers, because "Stop all" across
 * a filtered set is a much bigger action than the same button on one card.
 */
function BulkStopModal({ labs: selected, onClose, onDone }) {
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState([]);
  const totalRunning = selected.reduce((n, l) => n + (l.containerStats?.running || 0), 0);

  const run = async () => {
    const failed = [];
    for (let i = 0; i < selected.length; i++) {
      setProgress({ current: i + 1, total: selected.length, name: selected[i].name });
      try {
        await labs.stop(selected[i].id);
      } catch (err) {
        failed.push({ name: selected[i].name, message: err.response?.data?.message || err.message });
      }
    }
    setProgress(null);
    if (failed.length) setErrors(failed);
    else onDone();
  };

  return (
    <Dialog size="md" onClose={progress ? undefined : onClose} labelledBy="bulk-stop-title" confirmOnClose={false}>
      <DialogHeader id="bulk-stop-title" title="Stop containers" onClose={progress ? undefined : onClose} />
      <DialogBody>
        {errors.length > 0 ? (
          <div role="alert" className="dlg-alert">
            {errors.length} lab{errors.length === 1 ? '' : 's'} could not be stopped:
            <ul style={{ margin: '8px 0 0 18px' }}>
              {errors.map((e) => <li key={e.name}>{e.name} — {e.message}</li>)}
            </ul>
          </div>
        ) : (
          <>
            <p style={{ marginBottom: 12 }}>
              This stops every running container in {selected.length} lab
              {selected.length === 1 ? '' : 's'} — <strong>{totalRunning} container
              {totalRunning === 1 ? '' : 's'}</strong> in total. Student work is kept:
              only the pods stop, the workspaces stay.
            </p>
            <div style={{
              maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border-primary)',
              borderRadius: 8, padding: '8px 12px', fontFamily: 'var(--font-mono)', fontSize: 12,
            }}>
              {selected.map((l) => (
                <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span>{l.name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{l.containerStats?.running || 0} running</span>
                </div>
              ))}
            </div>
            {progress && (
              <p role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
                Stopping {progress.current} of {progress.total} — {progress.name}
              </p>
            )}
          </>
        )}
      </DialogBody>
      <DialogFooter>
        <button className="btn" onClick={onClose} disabled={Boolean(progress)}>
          {errors.length ? 'Close' : 'Cancel'}
        </button>
        {!errors.length && (
          <button className="btn btn-primary" onClick={run} disabled={Boolean(progress)}>
            {progress ? 'Stopping…' : `Stop ${totalRunning} container${totalRunning === 1 ? '' : 's'}`}
          </button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
